import React, { useMemo, useState, useEffect } from "react";
import {
    Grid,
    Paper,
    Typography,
    Box,
    Stack,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";

import {
    subscribeTeachers,
    addTeacher,
    updateTeacher,
    removeTeacher as removeTeacherFB,
} from "../../../services/teachers.service";

function Card({ title, subtitle, children }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid var(--border)",
                bgcolor: "var(--branco)",
                width: "100%",
            }}
        >
            <Typography sx={{ fontWeight: 900, color: "var(--cise-text)" }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ color: "var(--cise-text-muted)", fontSize: 13, mt: 0.3 }}>
                    {subtitle}
                </Typography>
            )}
            <Box sx={{ mt: 2 }}>{children}</Box>
        </Paper>
    );
}

function labelAgeGroup(v) {
    if (v === "kids") return "Criança";
    if (v === "adult") return "Adulto";
    return "Ambos";
}

function chipColorByAgeGroup(v) {
    if (v === "kids") return "info";
    if (v === "adult") return "warning";
    return "success";
}

function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim().toLowerCase());
}

const EMPTY_FORM = {
    name: "",
    email: "",
    ageGroup: "both", // kids | adult | both
    active: true,
};

export default function TeachersTab() {
    const [q, setQ] = useState("");

    // ✅ agora vem do Firebase
    const [teachers, setTeachers] = useState([]);

    // dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null); // null = add | string = edit
    const [form, setForm] = useState(EMPTY_FORM);

    // ✅ subscribe realtime
    useEffect(() => {
        const unsub = subscribeTeachers(setTeachers);
        return () => unsub?.();
    }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return teachers;

        return teachers.filter((t) =>
            String(t.name || "").toLowerCase().includes(term) ||
            String(t.email || "").toLowerCase().includes(term) ||
            labelAgeGroup(t.ageGroup).toLowerCase().includes(term)
        );
    }, [q, teachers]);

    const mode = editingId ? "edit" : "add";

    function openAddDialog() {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setOpenDialog(true);
    }

    function openEditDialog(teacher) {
        setEditingId(teacher.id);
        setForm({
            name: teacher.name || "",
            email: teacher.email || "",
            ageGroup: teacher.ageGroup || "both",
            active: !!teacher.active,
        });
        setOpenDialog(true);
    }

    function closeDialog() {
        setOpenDialog(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    }

    async function handleSubmitTeacher() {
        const name = form.name.trim();
        const email = form.email.trim();

        if (!name || !email) {
            alert("Preencha nome e e-mail.");
            return;
        }

        if (!isValidEmail(email)) {
            alert("Digite um e-mail válido.");
            return;
        }

        // evita duplicado por email (ignorando o próprio em modo edição)
        const emailLower = email.toLowerCase();
        const exists = teachers.some((t) => {
            const sameEmail = String(t.email || "").toLowerCase() === emailLower;
            const isOther = editingId ? t.id !== editingId : true;
            return sameEmail && isOther;
        });

        if (exists) {
            alert("Já existe um professor com esse e-mail.");
            return;
        }

        try {
            if (editingId) {
                await updateTeacher(editingId, {
                    name,
                    email,
                    ageGroup: form.ageGroup,
                    active: form.active,
                });
            } else {
                await addTeacher({
                    name,
                    email,
                    ageGroup: form.ageGroup,
                    active: form.active,
                });
            }

            closeDialog();
        } catch (err) {
            console.error(err);
            alert("Erro ao salvar professor no Firebase.");
        }
    }

    async function toggleActive(teacher) {
        try {
            await updateTeacher(teacher.id, { active: !teacher.active });
        } catch (err) {
            console.error(err);
            alert("Erro ao atualizar status no Firebase.");
        }
    }

    async function removeTeacher(teacher) {
        try {
            await removeTeacherFB(teacher.id);
        } catch (err) {
            console.error(err);
            alert("Erro ao remover professor no Firebase.");
        }
    }

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card title="Professores" subtitle="Cadastre professores e defina o público (criança/adulto)">
                        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                            <TextField
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="Buscar por nome, e-mail ou faixa..."
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 999,
                                        background: "var(--bg)",
                                    },
                                }}
                            />

                            <Button
                                variant="contained"
                                startIcon={<PersonAddAltOutlinedIcon />}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 900,
                                    bgcolor: "var(--cise-blue)",
                                    "&:hover": { bgcolor: "var(--cise-blue)" },
                                    px: 4,
                                }}
                                onClick={openAddDialog}
                            >
                                Adicionar
                            </Button>
                        </Stack>

                        <Paper elevation={0} sx={{ border: "1px solid var(--border)", borderRadius: 2, overflow: "hidden" }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "var(--bg)" }}>
                                        <TableCell sx={{ fontWeight: 900 }}>Nome</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>E-mail</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Faixa</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }} align="right">
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {filtered.map((t) => (
                                        <TeacherRow
                                            key={t.id}
                                            teacher={t}
                                            onToggle={() => toggleActive(t)}
                                            onRemove={() => removeTeacher(t)}
                                            onEdit={() => openEditDialog(t)}
                                        />
                                    ))}

                                    {filtered.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ color: "var(--cise-text-muted)" }}>
                                                Nenhum professor encontrado.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Card>
                </Grid>
            </Grid>

            {/* DIALOG (ADD/EDIT) */}
            <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 900 }}>
                    {mode === "edit" ? "Editar professor" : "Adicionar professor"}
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Nome do professor"
                            fullWidth
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />

                        <TextField
                            label="E-mail"
                            fullWidth
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            error={form.email.length > 0 && !isValidEmail(form.email)}
                            helperText={form.email.length > 0 && !isValidEmail(form.email) ? "E-mail inválido" : " "}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Faixa</InputLabel>
                            <Select
                                label="Faixa"
                                value={form.ageGroup}
                                onChange={(e) => setForm({ ...form, ageGroup: e.target.value })}
                            >
                                <MenuItem value="kids">Criança</MenuItem>
                                <MenuItem value="adult">Adulto</MenuItem>
                                <MenuItem value="both">Ambos</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={form.active ? "ativo" : "inativo"}
                                onChange={(e) => setForm({ ...form, active: e.target.value === "ativo" })}
                            >
                                <MenuItem value="ativo">Ativo</MenuItem>
                                <MenuItem value="inativo">Inativo</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={closeDialog}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitTeacher}
                        sx={{ fontWeight: 900, bgcolor: "var(--cise-blue)" }}
                    >
                        {mode === "edit" ? "Salvar" : "Adicionar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

function TeacherRow({ teacher, onToggle, onRemove, onEdit }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    return (
        <TableRow hover>
            <TableCell sx={{ fontWeight: 900 }}>{teacher.name}</TableCell>
            <TableCell>{teacher.email}</TableCell>

            <TableCell>
                <Chip
                    label={labelAgeGroup(teacher.ageGroup)}
                    size="small"
                    color={chipColorByAgeGroup(teacher.ageGroup)}
                    sx={{ fontWeight: 900 }}
                />
            </TableCell>

            <TableCell>
                <Chip
                    label={teacher.active ? "Ativo" : "Inativo"}
                    color={teacher.active ? "success" : "default"}
                    size="small"
                    sx={{ fontWeight: 900 }}
                />
            </TableCell>

            <TableCell align="right">
                <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{ sx: { borderRadius: 2 } }}
                >
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onToggle();
                        }}
                    >
                        {teacher.active ? "Desativar" : "Ativar"}
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onEdit();
                        }}
                    >
                        Editar
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            if (window.confirm("Remover este professor?")) onRemove();
                        }}
                        sx={{ color: "error.main" }}
                    >
                        Remover
                    </MenuItem>
                </Menu>
            </TableCell>
        </TableRow>
    );
}